import { Injectable, inject } from '@angular/core';
import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export enum WebSocketStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
  PAUSED = 'paused',
}

export const WS_CLOSE_UNAUTHORIZED = 4001;

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket: WebSocket | null = null;
  private messagesSubject = new Subject<any>();
  private statusSubject = new BehaviorSubject<WebSocketStatus>(WebSocketStatus.DISCONNECTED);

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private shouldReconnect = true;
  private isPaused = false;
  private currentEndpoint = '';
  private reconnectTimeout: any = null;
  // Set when the server closed with 4001; a new token (refresh or login) reconnects
  private closedUnauthorized = false;
  private authService = inject(AuthService);
  private refreshSubscription: Subscription | null = null;

  constructor() {
    this.listenForTokenRefresh();
  }

  private listenForTokenRefresh(): void {
    if (this.refreshSubscription) return;
    this.refreshSubscription = this.authService.getRefreshTokenSubject().subscribe((token) => {
      if (token && this.closedUnauthorized && this.currentEndpoint && !this.isPaused) {
        this.connect(this.currentEndpoint).subscribe();
      }
    });
  }

  /** wss on https pages, ws otherwise, keeping the host from the environment. */
  private socketUrl(endpoint: string): string {
    const configured = environment.wsUrl;
    if (typeof window === 'undefined') return `${configured}${endpoint}`;
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${configured.replace(/^wss?:/, scheme)}${endpoint}`;
  }

  connect(endpoint: string): Observable<any> {
    // Store the endpoint for reconnection purposes
    this.currentEndpoint = endpoint;
    // A caller connecting (page opened after login, token refreshed) starts a valid
    // session again: reconnecting is back on after an earlier 4001 or disconnect
    this.shouldReconnect = true;
    this.closedUnauthorized = false;
    this.listenForTokenRefresh();

    if (this.socket?.readyState === WebSocket.OPEN) {
      return this.messagesSubject.asObservable();
    }

    // Don't connect if paused
    if (this.isPaused) {
      console.log('⏸️ WebSocket is paused, connection attempt skipped');
      return this.messagesSubject.asObservable();
    }

    this.statusSubject.next(WebSocketStatus.CONNECTING);

    const token = localStorage.getItem('trading_ai_access_token');

    try {
      this.socket = new WebSocket(this.socketUrl(endpoint), this.authProtocols(token));
      this.setupSocketListeners(endpoint);
    } catch (error) {
      console.error('❌ WebSocket connection error:', error);
      this.statusSubject.next(WebSocketStatus.ERROR);
      this.attemptReconnect(endpoint);
    }

    return this.messagesSubject.asObservable();
  }

  /**
   * The only place the login token is attached to the socket (never log it).
   */
  private authProtocols(token: string | null): string[] | undefined {
    // Sec-WebSocket-Protocol: jwt, <token>; the server accepts with "jwt". The token
    // stays out of the URL, so out of access logs and browser history.
    return token ? ['jwt', token] : undefined;
  }

  /**
   *  Setup WebSocket listeners
   */
  private setupSocketListeners(endpoint: string): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log('WebSocket connected');
      this.statusSubject.next(WebSocketStatus.CONNECTED);
      this.reconnectAttempts = 0;
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.messagesSubject.next(data);
      } catch (error) {
        console.log('Error parsing message', error);
      }
    };

    this.socket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.statusSubject.next(WebSocketStatus.ERROR);
    };

    this.socket.onclose = (event: CloseEvent) => {
      console.log('🔌 WebSocket closed');

      // 4001 = the server refused the login: reconnecting cannot succeed
      if (event.code === WS_CLOSE_UNAUTHORIZED) {
        this.shouldReconnect = false;
        this.closedUnauthorized = true;
      }

      // Don't change status if paused
      if (!this.isPaused) {
        this.statusSubject.next(WebSocketStatus.DISCONNECTED);
      }

      if (this.shouldReconnect && !this.isPaused) {
        this.attemptReconnect(endpoint);
      }
    };
  }

  /**
   * Attempt to reconnect
   */
  protected attemptReconnect(endpoint: string): void {
    // Don't reconnect if paused
    if (this.isPaused) {
      console.log('⏸️ Reconnection skipped: WebSocket is paused');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.statusSubject.next(WebSocketStatus.ERROR);
      return;
    }

    this.reconnectAttempts++;
    this.statusSubject.next(WebSocketStatus.RECONNECTING);

    console.log(`🔄 Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = setTimeout(() => {
      this.connect(endpoint).subscribe();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  /**
   *  Send messaget throguht WebSocket
   */
  send(message: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const data = typeof message === 'string' ? message : JSON.stringify(message);
      this.socket.send(data);
    } else {
      console.warn('Cannot send message: WebSocket not connected');
    }
  }

  /**
   * Get connection status observable
   */
  getStatus(): Observable<WebSocketStatus> {
    return this.statusSubject.asObservable();
  }

  /**
   * check if connected
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = null;
    this.shouldReconnect = false;
    // A reconnect already scheduled must not undo the disconnect
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.statusSubject.next(WebSocketStatus.DISCONNECTED);
    this.reconnectAttempts = 0;
  }

  /**
   * Enable reconnection (call when returning to page)
   */
  enableReconnect(): void {
    this.shouldReconnect = true;
  }

  /**
   * Pause WebSocket connection
   * Closes the connection but maintains state for resuming
   */
  pause(): void {
    console.log('⏸️ Pausing WebSocket connection');
    this.isPaused = true;
    this.shouldReconnect = false;

    // Clear any pending reconnect attempts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close();
    }

    this.statusSubject.next(WebSocketStatus.PAUSED);
  }

  /**
   * Resume WebSocket connection
   * Reconnects using the last endpoint
   */
  resume(): void {
    console.log('▶️ Resuming WebSocket connection');
    this.isPaused = false;
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;

    if (this.currentEndpoint) {
      this.connect(this.currentEndpoint).subscribe();
    } else {
      console.warn('No endpoint stored for resuming connection');
    }
  }

  /**
   * Manual reconnect
   * Forces a reconnection attempt regardless of current state
   */
  reconnect(): void {
    console.log('🔄 Manual reconnect triggered');

    // Reset reconnect attempts
    this.reconnectAttempts = 0;
    this.shouldReconnect = true;
    this.isPaused = false;

    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Close existing connection if any
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    // Reconnect using stored endpoint
    if (this.currentEndpoint) {
      this.connect(this.currentEndpoint).subscribe();
    } else {
      console.warn('No endpoint stored for reconnection');
      this.statusSubject.next(WebSocketStatus.ERROR);
    }
  }

  /**
   * Check if WebSocket is paused
   */
  isPausedState(): boolean {
    return this.isPaused;
  }

  /**
   * Get current endpoint
   */
  getCurrentEndpoint(): string {
    return this.currentEndpoint;
  }
}
